import React, { useState, useRef, Fragment } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import BreadCrumbs from '../components/page/BreadCrumbs';
import HtmlHelmet from '../components/common/HtmlHelmet';
import { axiosNewInstance } from '../config/Api';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import toast from 'react-hot-toast';

/* ─── Password strength checklist component ─── */
const PasswordChecklist = ({ password }) => {
    const rules = [
        { label: 'At least 10 characters', test: password.length >= 10 },
        { label: 'At least one uppercase letter (A-Z)', test: /[A-Z]/.test(password) },
        { label: 'At least one lowercase letter (a-z)', test: /[a-z]/.test(password) },
        { label: 'At least one number (0-9)', test: /\d/.test(password) },
        { label: 'At least one special character (!@#$%^&*)', test: /[!@#$%^&*]/.test(password) },
    ];

    return (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0 0', fontSize: '0.85rem' }}>
            {rules.map((rule, idx) => (
                <li key={idx} style={{ color: rule.test ? '#28a745' : '#dc3545', marginBottom: '2px' }}>
                    {rule.test ? '✅' : '❌'} {rule.label}
                </li>
            ))}
        </ul>
    );
};


const RegisterOfficeForm = () => {
    const location = useLocation();
    const history = useHistory();

    // Pre-fill from LoginNew redirect (agent not yet registered)
    const locationState = location.state || {};
    const prefillMobile = locationState.mobile || '';
    const prefillAgencyCode = locationState.agencyCode || '';
    const prefillAgentName = locationState.agentName || '';
    const isAgentRedirect = !!prefillMobile;

    // OTP state (not part of formik)
    const [otpSent, setOtpSent] = useState(false);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '']);
    const [verifying, setVerifying] = useState(false);
    const ref0 = useRef(null);
    const ref1 = useRef(null);
    const ref2 = useRef(null);
    const ref3 = useRef(null);
    const inputRefs = [ref0, ref1, ref2, ref3];

    // General UI state
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    /* ─── Formik + Yup ─── */
    const formik = useFormik({
        initialValues: {
            name: prefillAgentName,
            mobile: prefillMobile,
            email: '',
            designation: '',
            password: '',
            confirmPassword: '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Name is required'),
            mobile: Yup.string().required('Mobile number is required'),
            email: Yup.string().email('Invalid email format'),
            designation: Yup.string(),
            password: Yup.string()
                .required('Password is required')
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{10,}$/,
                    'Password must be at least 10 characters, include one uppercase, one lowercase, one number, and one special character'
                ),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Passwords do not match')
                .required('Confirm Password is required'),
        }),
        onSubmit: () => {
            handleSendOtp();
        },
    });

    /* ─── OTP helpers ─── */
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newDigits = [...otpDigits];
        newDigits[index] = value.slice(-1);
        setOtpDigits(newDigits);
        if (value && index < 3) inputRefs[index + 1].current?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            inputRefs[index - 1].current?.focus();
        }
    };

    /* ─── Step 1: Send OTP ─── */
    const handleSendOtp = async () => {
        setLoading(true);
        setMessage('');

        try {
            if (formik.values.email) {
                const emailCheck = await axiosNewInstance.post('/api-app/check-email', {
                    email: formik.values.email,
                });
                if (emailCheck.data?.status === 1) {
                    setMessage(emailCheck.data.message || 'This email is already registered.');
                    setMessageType('danger');
                    setLoading(false);
                    return;
                }
            }

            const response = await axiosNewInstance.post('/api/send-mobile-otp', {
                phone: formik.values.mobile,
            });

            if (response.data && (response.data.response === 'sms-sent' || response.data === 'sms-sent')) {
                setMessage('OTP sent successfully!');
                setMessageType('success');
                setOtpSent(true);
                setOtpDigits(['', '', '', '']);
            } else {
                setMessage(response.data.message || 'Failed to send OTP.');
                setMessageType('danger');
            }
        } catch (error) {
            setMessage('Failed to send OTP.');
            setMessageType('danger');
        } finally {
            setLoading(false);
        }
    };

    /* ─── Step 2: Verify OTP ─── */
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otp = otpDigits.join('');
        if (otp.length !== 4) {
            setMessage('Please enter the 4-digit OTP.');
            setMessageType('danger');
            return;
        }
        setVerifying(true);
        setMessage('');
        try {
            const response = await axiosNewInstance.post('/api/validate-mobile-otp', {
                phone: formik.values.mobile,
                otp: otp,
            });
            if (response.data === 'right' || response.data.response === 'right') {
                try {
                    const regResp = await axiosNewInstance.post('/api-app/register-agent', {
                        en_first_name: formik.values.name,
                        mobile: formik.values.mobile,
                        email: formik.values.email || null,
                        designation: formik.values.designation || null,
                        password: formik.values.password,
                        password_confirmation: formik.values.confirmPassword,
                    });

                    if (regResp.data.status === 1) {
                        toast.success('Registration successful! Please login.');
                        history.push('/login-office');
                        return;
                    } else {
                        setMessage(regResp.data.message || 'Registration failed.');
                        setMessageType('danger');
                    }
                } catch (regErr) {
                    setMessage(regErr.response?.data?.message || 'Registration failed.');
                    setMessageType('danger');
                }
            } else {
                setMessage('Invalid OTP.');
                setMessageType('danger');
            }
        } catch (error) {
            setMessage('Failed to verify OTP.');
            setMessageType('danger');
        } finally {
            setVerifying(false);
        }
    };

    return (
        <section className="sign-in">
            <div className="container mt-4">
                <div className="card shadow-sm" style={{ maxWidth: '900px', margin: '0 auto 40px' }}>
                    <div className="card-header" style={{ backgroundColor: '#004e8f', color: 'white' }}>
                        <h3 className="mb-0">Office Registration</h3>
                    </div>
                    <div className="card-body">
                        {message && (
                            <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
                                {message}
                                <button type="button" className="close" onClick={() => setMessage('')}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        )}

                        {!otpSent ? (
                            <form onSubmit={formik.handleSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Name <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            name="name"
                                            className={`form-control ${formik.touched.name && formik.errors.name ? 'is-invalid' : ''}`}
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            readOnly={isAgentRedirect}
                                            style={isAgentRedirect ? { backgroundColor: '#e9ecef' } : {}}
                                        />
                                        {formik.touched.name && formik.errors.name && (
                                            <div className="invalid-feedback">{formik.errors.name}</div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Mobile <span className="text-danger">*</span></label>
                                        <input
                                            type="text"
                                            name="mobile"
                                            className={`form-control ${formik.touched.mobile && formik.errors.mobile ? 'is-invalid' : ''}`}
                                            value={formik.values.mobile}
                                            onChange={(e) => !isAgentRedirect && formik.setFieldValue('mobile', e.target.value.replace(/\D/g, ''))}
                                            onBlur={formik.handleBlur}
                                            readOnly={isAgentRedirect}
                                            style={isAgentRedirect ? { backgroundColor: '#e9ecef' } : {}}
                                        />
                                        {formik.touched.mobile && formik.errors.mobile && (
                                            <div className="invalid-feedback">{formik.errors.mobile}</div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">E-mail</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                            value={formik.values.email}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                        {formik.touched.email && formik.errors.email && (
                                            <div className="invalid-feedback">{formik.errors.email}</div>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Designation</label>
                                        <input
                                            type="text"
                                            name="designation"
                                            className="form-control"
                                            value={formik.values.designation}
                                            onChange={formik.handleChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Password <span className="text-danger">*</span></label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                className={`form-control ${formik.touched.password && formik.errors.password ? 'is-invalid' : ''}`}
                                                value={formik.values.password}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <span
                                                onClick={() => setShowPassword(!showPassword)}
                                                style={{ position: 'absolute', right: '10px', top: '10px', cursor: 'pointer', color: '#999' }}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </span>
                                        </div>
                                        {formik.touched.password && formik.errors.password && (
                                            <div className="invalid-feedback">{formik.errors.password}</div>
                                        )}
                                        {formik.values.password && <PasswordChecklist password={formik.values.password} />}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Confirm Password <span className="text-danger">*</span></label>
                                        <div style={{ position: 'relative' }}>
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                className={`form-control ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'is-invalid' : ''}`}
                                                value={formik.values.confirmPassword}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                            <span
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                style={{ position: 'absolute', right: '10px', top: '10px', cursor: 'pointer', color: '#999' }}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </span>
                                        </div>
                                        {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                                            <div className="invalid-feedback">{formik.errors.confirmPassword}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-4 d-flex justify-content-between align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={() => history.push('/login-office')}
                                    >
                                        Already have an account? Login
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={loading}>
                                        {loading ? 'Sending OTP...' : 'Send OTP & Register'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleVerifyOtp}>
                                <p className="text-center text-muted">Enter the 4-digit OTP sent to {formik.values.mobile}</p>
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '20px' }}>
                                    {[0, 1, 2, 3].map((index) => (
                                        <input
                                            key={index}
                                            ref={inputRefs[index]}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            className="form-control"
                                            style={{ width: '55px', textAlign: 'center', fontSize: '1.5rem' }}
                                            value={otpDigits[index]}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        />
                                    ))}
                                </div>
                                <div className="text-center">
                                    <button type="submit" className="btn btn-primary" disabled={verifying}>
                                        {verifying ? 'Verifying...' : 'Verify OTP'}
                                    </button>
                                </div>
                                <div className="text-center mt-2">
                                    <button type="button" className="btn btn-link" onClick={() => { setOtpSent(false); setMessage(''); }}>
                                        ← Back to Form
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const RegisterOffice = () => {
    const meta_data = {
        meta_title: 'Office Register',
        meta_description: 'Register a new office account',
    }

    const bread_cumbs = [
        { title: "Office Register", url: "/register-office" },
        { title: "Home", url: "/" }
    ];

    return (
        <Fragment>
            <HtmlHelmet custom_meta={meta_data} />
            <BreadCrumbs bread_curmbs={bread_cumbs} />
            <RegisterOfficeForm />
        </Fragment>
    );
};

export default RegisterOffice;
